// First login from an invitation or password-reset link (BD-73): "Set up your sign-in". Step 1 registers
// Face ID or fingerprint on this device; step 2 sets a password as the backup; then Today opens. A link
// that expired or was already used says so and how to get a new one.
import { useState } from "react";
import { CircleCheck, Fingerprint, KeyRound } from "lucide-react";
import { useAuth, passkeySupported } from "./AuthProvider";
import { ApiError } from "../lib/api";
import { BrandScreen } from "../brand/Brand";
import { Button, ErrorNote, Field, Notice, TextInput } from "../ui/primitives";

const MIN_LENGTH = 10;

export function SetUpSignIn() {
  const { me, registerPasskey, setPassword, finishSetUp } = useAuth();
  const canPasskey = passkeySupported();
  const [step, setStep] = useState<"passkey" | "password">(canPasskey && !me?.has_passkey ? "passkey" : "password");
  const [registered, setRegistered] = useState(!!me?.has_passkey);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const steps = canPasskey ? 2 : 1;
  const firstName = me?.full_name.split(" ")[0];

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(e instanceof ApiError ? e.message : fallback); }
    finally { setBusy(false); }
  };

  return (
    <BrandScreen heading={false} testid="set-up-sign-in">
      <h1 className="text-[30px] leading-tight">Set up your sign-in</h1>
      <p className="mt-2 text-[17px] text-muted">
        {firstName ? `Welcome, ${firstName}. ` : ""}
        {canPasskey ? "Two steps: Face ID or fingerprint on this device, then a password as a backup for other devices." : "Choose a password to sign in with."}
      </p>
      {error && <ErrorNote className="mt-4" message={error} code="invalid" />}

      {step === "passkey" ? (
        <section className="mt-8" aria-labelledby="step-passkey">
          <p className="text-[13px] font-medium uppercase tracking-wide text-muted">Step 1 of {steps}</p>
          <h2 id="step-passkey" className="mt-1 text-[22px]">Face ID or fingerprint</h2>
          <p className="mt-1 text-muted">Register this device once, and sign in with a glance from now on.</p>
          <div className="mt-5 space-y-3">
            <Button size="lg" block busy={busy} icon={<Fingerprint className="h-5 w-5" aria-hidden />} onClick={() => run(async () => {
              await registerPasskey();
              setRegistered(true);
              setStep("password");
            }, "Registration was cancelled or did not work. Try again.")}>Register Face ID or fingerprint</Button>
            <Button variant="ghost" block onClick={() => { sessionStorage.setItem("rk-skip-register", "1"); setError(null); setStep("password"); }}>This device has no Face ID or fingerprint</Button>
          </div>
        </section>
      ) : (
        <section className="mt-8" aria-labelledby="step-password">
          {registered && <Notice tone="info" className="mb-5"><CircleCheck className="mr-1 inline h-4 w-4" aria-hidden />Face ID or fingerprint is registered on this device.</Notice>}
          <p className="text-[13px] font-medium uppercase tracking-wide text-muted">Step {steps} of {steps}</p>
          <h2 id="step-password" className="mt-1 text-[22px]">A password as a backup</h2>
          <p className="mt-1 text-muted">For a device without Face ID or fingerprint. At least {MIN_LENGTH} characters.</p>
          <form className="mt-5 space-y-4" onSubmit={(e) => {
            e.preventDefault();
            if (pw.length < MIN_LENGTH) { setError(`The password needs at least ${MIN_LENGTH} characters.`); return; }
            if (pw !== pw2) { setError("The two passwords are not the same. Type them again."); return; }
            void run(async () => { await setPassword(pw); finishSetUp(); }, "The password could not be saved. Try again.");
          }}>
            {me?.email && <input type="email" name="username" autoComplete="username" value={me.email} readOnly hidden />}
            <Field label="New password" htmlFor="new-password">
              <TextInput id="new-password" type="password" autoComplete="new-password" required value={pw} onChange={(e) => setPw(e.target.value)} />
            </Field>
            <Field label="Type the password again" htmlFor="new-password-2">
              <TextInput id="new-password-2" type="password" autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} />
            </Field>
            <Button type="submit" size="lg" block busy={busy} icon={<KeyRound className="h-5 w-5" aria-hidden />}>Save the password and open Today</Button>
          </form>
        </section>
      )}
    </BrandScreen>
  );
}

export function LinkProblem() {
  const { clearLinkProblem } = useAuth();
  return (
    <BrandScreen heading={false} testid="link-problem">
      <h1 className="text-[28px] leading-tight">This link no longer works</h1>
      <p className="mt-2 text-[17px]">The link in your email has expired or was already used. Each invitation or reset link works once, and only for a limited time.</p>
      <h2 className="mt-6 text-[18px]">To get in</h2>
      <ul className="mt-2 list-disc space-y-2 pl-5 text-muted">
        <li>If you already set up Face ID, fingerprint or a password, sign in as usual.</li>
        <li>Otherwise ask the CEO or a general manager for a new invitation or a temporary password. If you are the CEO, ask the person who looks after the system to send you a new invitation.</li>
      </ul>
      <Button className="mt-8" size="lg" block onClick={clearLinkProblem}>Go to sign-in</Button>
    </BrandScreen>
  );
}
